/**
 * Pure helpers for the AqiBadge component —
 * extracted for unit testing.
 */

export interface TrendPoint {
  at: string;
  aqi: number | null;
  pm25?: number | null;
}

export interface AqiTrendInput {
  current: { aqi: number | null };
  next8h: TrendPoint[];
}

/**
 * Compute the maximum AQI over the current reading + next-8-hour forecast.
 * Falls back to current AQI (or 0) when forecast points have null AQI.
 */
export function maxAqiNext8h(trend: AqiTrendInput): number {
  return trend.next8h.reduce(
    (m, p) => Math.max(m, p.aqi ?? m),
    trend.current.aqi ?? 0,
  );
}

/**
 * Format an ISO timestamp to a zero-padded hour string, e.g. "08h", "14h".
 */
export function fmtHour(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours().toString().padStart(2, "0")}h`;
}

/**
 * AirDash airCctv.pm25Band, Thai PCD 24-hour PM2.5 bands (2023).
 *
 * The band names are the PCD names in English: ดีมาก is "Very good", not
 * "Excellent" — the string is shown beside a "Thai PCD" label, so it has to
 * be the authority's own vocabulary.
 */
export function thaiPm25Band(value: number | null): string | null {
  if (value === null || !Number.isFinite(value) || value < 0) return null;
  if (value <= 15) return "Very good";
  if (value <= 25) return "Good";
  if (value <= 37.5) return "Moderate";
  if (value <= 75) return "Starts affecting health";
  return "Affects health";
}
